// Angular
import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

// RxJS
import { catchError, map, Observable, of, switchMap } from 'rxjs';

// Local
import { UserManagementApiService } from '@app/services/api/user-management/user-management.service';
import { EmulationService } from '@core/auth/emulation.service';
import { ROUTES } from '@routes';

/**
 * Whether the user's court enables judge chambers (`UserPermissionList.isJudgeChambersEnabled`).
 * Falls back to `false` when the permission list cannot be loaded, so users land
 * on the standard Judge Review list. Must be called in an injection context.
 */
function isJudgeChambersEnabled(): Observable<boolean> {
  const emulationService = inject(EmulationService);
  const userManagementApiService = inject(UserManagementApiService);

  return emulationService.ensureReady().pipe(
    switchMap(() => userManagementApiService.getUserPermissions()),
    map(permissions => permissions.isJudgeChambersEnabled ?? false),
    catchError(() => of(false)),
  );
}

/**
 * On the Judge Review list route: sends users whose court enables judge
 * chambers to the Judge Chambers page instead, keeping query params (e.g. the
 * dashboard `documentTypeId` deep-link). Every existing link to the list then
 * lands on the right page.
 */
export const judgeChambersRedirectGuard: CanActivateFn = route => {
  const router = inject(Router);

  return isJudgeChambersEnabled().pipe(
    map(enabled =>
      enabled
        ? router.createUrlTree(['/', ROUTES.JUDGE_CHAMBERS], {
            queryParams: route.queryParams,
          })
        : true,
    ),
  );
};

/**
 * On the Judge Chambers route: sends users whose court does not enable judge
 * chambers back to the Judge Review list, keeping query params.
 */
export const judgeChambersEnabledGuard: CanActivateFn = route => {
  const router = inject(Router);

  return isJudgeChambersEnabled().pipe(
    map(enabled =>
      enabled
        ? true
        : router.createUrlTree(['/', ROUTES.JUDGE_REVIEW_LIST], {
            queryParams: route.queryParams,
          }),
    ),
  );
};
